import React from 'react';
import { useField } from 'react-final-form';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Intent } from '@motorway/mw-highway-code/src/types';
import { InputVRM } from '@THC/components/InputVRM';

import { coerceVRM, composeValidators, getFieldState, isValidVRM, requiredValidator, VRMValidator } from 'Utilities/formValidators';

import { FeedbackTooltip } from 'Storybook/components/local/FeedbackTooltip/FeedbackTooltip';

import Texts from '../../../../../texts.json';

const LocalT = new MDText(Texts);

type VRMInputProps = {
  className?: string;
  disabled?: boolean;
  e2eName?: string;
  hideIntent?: boolean;
  large?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInputBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onInputFocus?: () => void;
  required?: boolean;
  vrmId?: string;
};

export const VRMInput = ({
  className,
  disabled = false,
  e2eName,
  hideIntent = false,
  large = false,
  onChange = () => { },
  onInputBlur = () => { },
  onInputFocus = () => { },
  required,
  vrmId = '',
}: VRMInputProps) => {
  const { input, meta } = useField('vrm', {
    format: (value) =>
      (isValidVRM(value) ? coerceVRM(value) : value),
    initialValue: '',
    parse: (value) => value.toLocaleUpperCase(),
    validate: composeValidators(
      requiredValidator(
        LocalT.translate('components.vrmChecker.errorMessage'),
      ),
      VRMValidator(
        LocalT.translate('components.vrmChecker.errorMessage'),
      ),
    ),
  });

  const intent = getFieldState(meta, false);
  const id = `vrm-input${vrmId ? `-${vrmId}` : ''}`;
  const shouldHideIntent = hideIntent && intent === 'success';

  return (
    <FeedbackTooltip appendTo='parent' content={meta.error} intent={intent} placement='top' zIndex={10}>
      <div className={cx(className)}>
        <InputVRM
          fullWidth
          formProps={{
            input: {
              ...input,
              autoCapitalize: 'characters',
              autoCorrect: 'off',
              'data-cy': 'vrmCheckers',
              'data-e2ename': e2eName,
              disabled,
              maxLength: 10,
              onBlur: (e?: React.FocusEvent<HTMLElement, Element>) => {
                input.onBlur(e);
                if (meta.invalid && e) {
                  onInputBlur?.(e as React.FocusEvent<HTMLInputElement>);
                }
              },
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                input.onChange(e);
                onChange?.(e);
              },
              onFocus: (e?: React.FocusEvent<HTMLElement, Element>) => {
                input.onFocus(e);
                onInputFocus?.();
              },
              placeholder: LocalT.translate('components.vrmChecker.placeholder'),
              required,
            },
          }}
          id={id}
          intent={shouldHideIntent ? undefined : intent as Intent}
          label=""
          showLabel={false}
          size={large ? 'large' : undefined}
        />
      </div>
    </FeedbackTooltip>
  );
};
